import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, FileText, Loader2, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Simulator } from "@/components/Simulator";
import { InvestorBrochure } from "@/components/InvestorBrochure";
import { RequestForm } from "@/components/RequestForm";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/assets";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SimulatorPage() {
  const { t, lang } = useLang();
  const s = t.simulator;
  const m = s.bpModal;
  const [sim, setSim] = useState(null);
  const [paying, setPaying] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", city: "" });

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const submitBuyer = async (e) => {
    e.preventDefault();
    if (!buyer.name.trim() || !emailValid(buyer.email.trim())) {
      toast.error(m.required);
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "business_plan",
        origin_url: window.location.origin,
        sim_params: sim,
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim(),
        city: buyer.city.trim(),
        location: buyer.city.trim(),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(s.error || "Errore nel pagamento, riprova.");
      setPaying(false);
    }
  };

  return (
    <>
      <Seo
        path="/simulatore"
        lang={lang}
        image={IMAGES.simHero}
        title={{
          it: "Simulatore Investimento RISORICE® — Rata, break-even e ROI",
          en: "RISORICE® Investment Simulator — Instalment, break-even and ROI",
          ru: "Калькулятор инвестиций RISORICE® — Платёж, безубыточность и ROI",
        }[lang]}
        description={{
          it: "Simula l'investimento per aprire un RISORICE®: modalità di finanziamento, rata mensile, break-even e payback. Richiedi il Business Plan personalizzato da € 99.",
          en: "Simulate the investment to open a RISORICE®: financing mode, monthly instalment, break-even and payback. Request the custom €99 Business Plan.",
          ru: "Рассчитайте инвестиции для открытия RISORICE®: способ финансирования, ежемесячный платёж, безубыточность и окупаемость. Закажите персональный бизнес-план за € 99.",
        }[lang]}
      />
      <section className="relative pt-28 md:pt-36 pb-10 bg-ink text-white overflow-hidden" data-testid="simulator-hero">
        <img src={IMAGES.simHero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/50" />
        <div className="relative mx-auto max-w-[1280px] px-5 md:px-8">
          <span className="pill bg-white/10 text-tricolor-green border border-tricolor-green/30">{s.kicker}</span>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[0.95] max-w-3xl">{s.title}</h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">{s.subtitle}</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-surface" data-testid="simulator-section">
        <div className="mx-auto max-w-[1000px] px-5 md:px-8">
          <Simulator onSummary={setSim} />
        </div>
      </section>

      <InvestorBrochure />

      <section className="py-16 md:py-24 bg-white" data-testid="businessplan-section">
        <div className="mx-auto max-w-[1000px] px-5 md:px-8">
          <Reveal>
            <div className="rounded-[2rem] border border-line overflow-hidden grid lg:grid-cols-2">
              <div className="p-8 md:p-12 bg-ink text-white">
                <span className="pill bg-tricolor-green text-white">{s.bpBadge}</span>
                <h2 className="mt-5 font-display font-extrabold text-3xl md:text-4xl leading-tight">{s.bpTitle}</h2>
                <p className="mt-4 text-white/60 leading-relaxed">{s.bpBody}</p>
                <div className="mt-8 flex items-end gap-3">
                  <span className="font-display font-extrabold text-5xl text-tricolor-green">{s.bpPrice}</span>
                  <span className="text-white/50 mb-2 text-sm">{s.bpPriceNote}</span>
                </div>
                <button onClick={() => setModalOpen(true)} className="btn-primary mt-6" data-testid="bp-buy-btn">
                  <FileText size={18} /> {s.bpButton}
                </button>
              </div>
              <div className="p-8 md:p-12 bg-surface">
                <ul className="space-y-4">
                  {s.bpFeatures.map((f, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`bp-feature-${i}`}>
                      <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-tricolor-green text-white flex items-center justify-center"><Check size={14} /></span>
                      <span className="text-ink/80">{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-8 text-sm text-muted leading-relaxed border-t border-line pt-6">{s.bpNote}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="richiedi-bp" className="py-20 md:py-28 bg-surface" data-testid="simulator-form-section">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-ink">{s.ctaTitle} <span className="text-tricolor-green">🚀</span></h2>
            <p className="mt-3 text-muted">{s.ctaBody}</p>
          </div>
          <RequestForm title={t.franchisingPage.formTitle} subtitle={t.franchisingPage.formSubtitle} simParams={sim} defaultMessage="Richiesta info dal simulatore investimento." />
        </div>
      </section>

      {modalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="bp-modal-wrap">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { if (!paying) setModalOpen(false); }} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 md:p-8 shadow-2xl" data-testid="bp-modal">
            <button type="button" onClick={() => { if (!paying) setModalOpen(false); }} className="absolute top-4 right-4 text-muted hover:text-ink transition-colors" data-testid="bp-modal-close" aria-label="Close">
              <X size={20} />
            </button>
            <h3 className="font-display font-extrabold text-2xl text-ink pr-8">{m.title}</h3>
            <p className="mt-2 text-sm text-muted leading-relaxed">{m.subtitle}</p>
            <form onSubmit={submitBuyer} className="space-y-4 mt-5">
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">{m.name} *</label>
                <input type="text" required value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="bp-modal-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">{m.email} *</label>
                <input type="email" required value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                  className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="bp-modal-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-ink mb-1.5">{m.phone}</label>
                  <input type="tel" value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                    className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="bp-modal-phone" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink mb-1.5">{m.city}</label>
                  <input type="text" value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })}
                    className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="bp-modal-city" />
                </div>
              </div>
              <button type="submit" disabled={paying} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="bp-modal-submit">
                {paying ? <><Loader2 size={18} className="animate-spin" /> ...</> : <><FileText size={18} /> {m.submit}</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
